import React from 'react';
import { object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { StaticPage, TopbarContainer } from '..';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  MarketplaceProofStats,
  SectionSuburbTrailers,
  SectionSuburbListings,
  SectionSuburbReviews,
  SectionSuburbSnapshot,
  SectionSuburbFaq,
  SectionSuburbHowToHire,
  SectionUsefulGuides,
  NearbySuburbsSection,
  MultiFilterSearchBar,
  NamedLink,
  NamedRedirect,
} from '../../components';
import {
  buildSuburbLandingIntro,
  buildSuburbLandingPageHeading,
  buildSuburbLandingPageTitle,
  getSuburbLandingPage,
  getSuburbLandingSlugRedirect,
} from '../../util/suburbLandingPages';
import {
  buildSuburbLandingBreadcrumbItems,
  createSuburbLandingBreadcrumbSchema,
} from '../../util/trailerHireBreadcrumbs';
import { getSuburbLandingPageGuideLinks } from '../../util/trailerHirePageBrowseLinks';
import { MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY } from '../../marketplace-custom-config';
import { resolveSuburbLandingInventoryScope } from '../../util/marketplaceProofStats';

import css from './SuburbLandingPage.module.css';

const renderBreadcrumbLink = item => {
  if (item.current || !item.link) {
    return (
      <span className={css.breadcrumbCurrent} aria-current="page">
        {item.label}
      </span>
    );
  }

  return (
    <NamedLink name={item.link.name} params={item.link.params} className={css.breadcrumbLink}>
      {item.label}
    </NamedLink>
  );
};

export const SuburbLandingPageComponent = props => {
  const { history, intl, match, previewSnapshot = null } = props;
  const slug = match?.params?.slug;
  const redirectSlug = getSuburbLandingSlugRedirect(slug);

  if (redirectSlug) {
    return <NamedRedirect name="SuburbLandingPage" params={{ slug: redirectSlug }} />;
  }

  const suburb = getSuburbLandingPage(slug);

  if (!suburb) {
    return <NamedRedirect name="NotFoundPage" />;
  }

  const { name, metaDescription, parentLandingPage = null } = suburb;
  const pageTitle = buildSuburbLandingPageTitle(name, previewSnapshot);
  const pageHeading = buildSuburbLandingPageHeading(name);
  const intro = buildSuburbLandingIntro(name);
  const guideLinks = getSuburbLandingPageGuideLinks(intl);
  const breadcrumbItems = buildSuburbLandingBreadcrumbItems(suburb);
  const breadcrumbSchema = createSuburbLandingBreadcrumbSchema(suburb);
  const pageSchema = [
    {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      description: metaDescription,
      name: pageTitle,
    },
    breadcrumbSchema,
  ].filter(Boolean);

  return (
    <StaticPage title={pageTitle} description={metaDescription} schema={pageSchema}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={`${css.root} ${css.suburbLanding}`}>
          <div className={css.hero}>
            {breadcrumbItems.length > 0 ? (
              <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                <ol className={css.breadcrumbList}>
                  {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      {index > 0 ? (
                        <li className={css.breadcrumbSeparatorItem} aria-hidden="true">
                          <span className={css.breadcrumbSeparator} />
                        </li>
                      ) : null}
                      <li className={css.breadcrumbItem}>{renderBreadcrumbLink(item)}</li>
                    </React.Fragment>
                  ))}
                </ol>
              </nav>
            ) : null}

            <h1 className={css.pageTitle}>{pageHeading}</h1>
            <MarketplaceProofStats
              inventoryScope={resolveSuburbLandingInventoryScope(suburb)}
              listingCount={previewSnapshot?.totalCount}
              minPriceAmount={previewSnapshot?.minPriceAmount}
              currency={previewSnapshot?.currency || 'AUD'}
              locationName={name}
              reviewCountDisplay={MARKETPLACE_COMPLETED_HIRE_REVIEWS_COUNT_DISPLAY}
            />
            <p className={css.intro}>{intro}</p>

            <section className={css.searchSection} aria-label={`Search trailers in ${name}`}>
              <h2 className={css.searchHeading}>Search trailers near you</h2>
              <div className={css.searchBar}>
                <MultiFilterSearchBar history={history} initialSuburb={suburb} />
              </div>
            </section>
          </div>

          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbListings suburb={suburb} />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbReviews suburb={suburb} />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbSnapshot suburb={suburb} />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbTrailers suburb={suburb} />
              </div>
            </li>

            {suburb.nearbySuburbs?.length > 0 ? (
              <li className={css.section}>
                <div className={css.sectionContent}>
                  <NearbySuburbsSection
                    currentLocationName={name}
                    nearbySuburbs={suburb.nearbySuburbs}
                    parentStateLabel={suburb.state}
                    showIntro={!suburb.isPrimaryCity}
                  />
                </div>
              </li>
            ) : null}

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbHowToHire suburb={suburb} />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionSuburbFaq suburb={suburb} />
              </div>
            </li>

            {parentLandingPage ? (
              <li className={css.section}>
                <div className={css.sectionContent}>
                  <p className={css.parentLandingPage}>
                    <NamedLink
                      name="SuburbLandingPage"
                      params={{ slug: parentLandingPage.slug }}
                      className={css.parentLandingPageLink}
                    >
                      Browse all trailers for hire across {parentLandingPage.name}
                    </NamedLink>
                  </p>
                </div>
              </li>
            ) : null}

            {guideLinks.length ? (
              <li className={css.section}>
                <div className={css.sectionContent}>
                  <SectionUsefulGuides
                    headingId="suburb-advice-heading"
                    headingMessageId="SuburbLandingPage.guidesHeading"
                    introMessageId="SuburbLandingPage.guidesIntro"
                    introMessageValues={{ suburb: name }}
                    guides={guideLinks}
                    showAdviceHubLink
                  />
                </div>
              </li>
            ) : null}
          </ul>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

SuburbLandingPageComponent.propTypes = {
  history: object.isRequired,
  intl: intlShape.isRequired,
  match: object.isRequired,
  previewSnapshot: object,
};

const mapStateToProps = (state, ownProps) => {
  const slug = ownProps.match?.params?.slug;
  const { previewSnapshot, previewSlug } = state.SuburbLandingPage || {};
  const isCurrentSuburb = previewSlug === slug;

  return {
    previewSnapshot: isCurrentSuburb ? previewSnapshot : null,
  };
};

const SuburbLandingPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(SuburbLandingPageComponent);

export default SuburbLandingPage;
